import { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "./ui/resizable";
import { Button } from "./ui/button";
import { X, Minimize2 } from "lucide-react";
import { Submenu, SubmenuItem } from "./submenu";
import { handleCloseParam } from "@/components/close-rightsidebar";
import { DynamicBreadcrumbs } from "./breadcrumb";
import { FullscreenSidebar, GlobalFullscreenDialog, globalFullscreenState } from "./fullscreen-sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";

interface SubmenuContextType {
  items: SubmenuItem[];
  itemsLoading?: boolean;
  title: string;
  active: boolean;
  placeholder?: string;
  queryOptions?: any;
  createNew?: {
    label: string;
    href: string;
    params?: any;
  }[];
  onClick?: (item: SubmenuItem, functionType: string) => void;
}

interface DashboardContextType {
  rightSidebar: React.ReactNode | null;
  setRightSidebar: (value: React.ReactNode | null) => void;
  submenu: SubmenuContextType | null;
  setSubmenu: (submenu: SubmenuContextType | null) => void;
  rightSidebarParamKey?: any;
  setRightSidebarParamKey?: (value: string | string[] | undefined) => void;
}

export const DashboardContext = createContext<DashboardContextType | null>(null);

export function RightSidebarPanel() {
  const { navigate } = useRouter();
  const dashboardContext = useContext(DashboardContext);
  const isMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = useState(() => globalFullscreenState.isOpen);

  useEffect(() => {
    const unsubscribe = globalFullscreenState.subscribe(() => {
      setIsFullscreen(globalFullscreenState.isOpen);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    return () => {
      if (globalFullscreenState.isOpen) {
        globalFullscreenState.setOpen(false);
      }
    };
  }, []);

  if (!dashboardContext || !dashboardContext.rightSidebar) return null;

  const { rightSidebar, setRightSidebar, rightSidebarParamKey } = dashboardContext;

  // On mobile, use Sheet instead of fixed panel
  if (isMobile === true) {
    return null;
  }

  const handleClose = () => {
    if (globalFullscreenState.isOpen) {
      globalFullscreenState.setOpen(false);
    }
    handleCloseParam({
      paramToRemove: rightSidebarParamKey,
      setSidebar: setRightSidebar,
      navigate,
    });
  };

  return (
    <>
      <ResizableHandle withHandle={!!rightSidebar && !isFullscreen} />
      <ResizablePanel
        id="right-sidebar-panel"
        order={3}
        defaultSize={30}
        minSize={20}
        maxSize={60}
        className="shadow-lg mmx-content-right pt-2 right-sidebar"
        hidden={!rightSidebar}
      >
        {isFullscreen &&
          createPortal(
            <div
              aria-hidden
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => globalFullscreenState.setOpen(false)}
            />,
            document.body
          )}

        <div
          className={
            isFullscreen
              ? "fixed inset-20 z-[51] bg-background border rounded-lg shadow-xl overflow-hidden flex flex-col pt-2"
              : "flex flex-col h-full"
          }
        >
          <div className="pr-3 w-full flex justify-end gap-1.5 mb-1 shrink-0">
            {isFullscreen ? (
              <Button
                variant="outline"
                size="icon"
                title="Collapse to sidebar"
                onClick={() => globalFullscreenState.setOpen(false)}
              >
                <Minimize2 />
              </Button>
            ) : (
              <FullscreenSidebar>{rightSidebar}</FullscreenSidebar>
            )}
            <Button variant="outline" size="icon" title="Close sidebar" onClick={handleClose}>
              <X />
            </Button>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden">{rightSidebar}</div>
        </div>
      </ResizablePanel>
    </>
  );
}

export function SubmenuPanel() {
  const { submenu } = useContext(DashboardContext) || { submenu: null, setSubmenu: null };

  if (!submenu?.active) return null;

  return (
    <>
      <ResizablePanel
        id="submenu-panel"
        order={1}
        className="mmx-content-main p-4 overflow-y-auto submenu"
        defaultSize={15}
        minSize={15}
        maxSize={50}
      >
        <div className="flex flex-col justify-between mmx-sidebar">
          <Submenu
            itemsLoading={submenu.itemsLoading}
            items={submenu.items}
            title={submenu.title}
            placeholder={submenu.placeholder}
            createNew={submenu.createNew}
            onClick={(item, type) => {
              submenu.onClick?.(item, type ?? "");
            }}
            queryOptions={submenu.queryOptions}
          />
        </div>
      </ResizablePanel>
      <ResizableHandle withHandle={false} />
    </>
  );
}

export function DashboardLayout({
  children,
  submenu = null,
  rightSidebarContent = null,
  itemsLoading = false,
}: {
  children: React.ReactNode;
  submenu?: SubmenuContextType | null;
  rightSidebarContent?: React.ReactNode | null;
  itemsLoading?: boolean;
}) {
  const [rightSidebar, setRightSidebar] = useState<React.ReactNode | null>(rightSidebarContent);
  const [rightSidebarParamKey, setRightSidebarParamKey] = useState<string | string[] | undefined>(undefined);
  const [submenuState, setSubmenu] = useState<SubmenuContextType | null>(submenu);
  const lastClosedPathRef = useRef<string | null>(null);
  const lastOpenedRootRef = useRef<string | null>(null);

  const { navigate } = useRouter();
  const locationState = useRouterState({ select: (s) => s.location }) as {
    pathname: string;
    search?: Record<string, unknown>;
  };
  const isMobile = useIsMobile();

  useEffect(() => {
    setRightSidebar(rightSidebarContent);
  }, [rightSidebarContent]);

  // Update submenu state when the submenu prop changes
  useEffect(() => {
    if (submenu) {
      setSubmenu(submenu);
    }
  }, [submenu]);

  //  Dynamic submenu visibility logic
  useEffect(() => {
    if (!submenuState || !setSubmenu) return;

    // Don't run submenu logic until mobile detection is complete
    if (isMobile === undefined) return;

    const currentPath = locationState?.pathname ?? "";
    const pathSegments = currentPath.split("/").filter(Boolean);

    const base = pathSegments[0] || "";
    const depth = pathSegments.length;

    // Routes with "new-" in the path or "new" in search params should be treated as nested routes to close submenu
    const isNewRoute =
      currentPath.includes("/new-") ||
      (locationState?.search &&
        typeof locationState.search === "object" &&
        "id" in locationState.search &&
        (locationState.search as any).id === "new");

    // Check if we're on a detail page (has id parameter and not "new")
    const hasIdParam =
      locationState?.search &&
      typeof locationState.search === "object" &&
      "id" in locationState.search &&
      (locationState.search as any).id &&
      (locationState.search as any).id !== "new";

    const isMainRoute = depth <= 2 && !isNewRoute && !hasIdParam;

    if (isMobile === true) {
      if (isMainRoute) {
        const sameRoot = lastOpenedRootRef.current === base;
        if (!submenuState.active || !sameRoot) {
          setSubmenu({
            ...submenuState,
            active: true,
          });
          lastOpenedRootRef.current = base;
          lastClosedPathRef.current = null;
        }
      } else {
        setSubmenu({ ...submenuState, active: false });
      }
    } else {
      if (!submenuState.active) {
        setSubmenu({
          ...submenuState,
          active: true,
        });
      }
    }
  }, [locationState?.pathname, locationState?.search, isMobile]);

  const mainContentSize = useMemo(() => {
    if (isMobile === true) return 100;
    // During hydration, assume full width to prevent layout shift
    if (isMobile === undefined) return 100;

    const hasSubmenu = submenuState?.active;
    const hasRightSidebar = !!rightSidebar;

    if (hasSubmenu && hasRightSidebar) {
      return 55; // 15 (submenu) + 55 (main) + 30 (right sidebar) = 100
    } else if (hasSubmenu) {
      return 85; // 15 (submenu) + 85 (main) = 100
    } else if (hasRightSidebar) {
      return 70; // 70 (main) + 30 (right sidebar) = 100
    } else {
      return 100; // 100 (main only) = 100
    }
  }, [submenuState?.active, rightSidebar, isMobile]);

  const contextValue = useMemo(
    () => ({
      rightSidebar,
      setRightSidebar,
      submenu: submenuState,
      setSubmenu,
      rightSidebarParamKey,
      setRightSidebarParamKey,
    }),
    [submenuState, rightSidebarParamKey, rightSidebar]
  );

  const closeSubmenu = () => {
    if (submenuState) {
      setSubmenu({ ...submenuState, active: false });
    }
  };

  // Show minimal layout during hydration to prevent flash
  if (isMobile === undefined) {
    return (
      <div className="w-full flex h-full">
        <DashboardContext.Provider value={contextValue}>
          <div className="w-full mmx-content flex flex-col h-full">
            <div className="h-full overflow-y-auto">
              <div className="p-4 flex flex-col gap-4">
                <div className="flex shrink-0 items-center">
                  <div className="flex items-center gap-2 mb-3">
                    <DynamicBreadcrumbs />
                  </div>
                </div>
                {children}
              </div>
            </div>
          </div>
          <GlobalFullscreenDialog />
        </DashboardContext.Provider>
      </div>
    );
  }

  // Handle small screens (mobile & small tablets)
  if (isMobile === true) {
    return (
      <div className="w-full flex flex-col h-full">
        <DashboardContext.Provider value={contextValue}>
          {/* Left submenu drawer */}
          {submenuState?.active && (
            <Sheet
              open={submenuState.active}
              onOpenChange={(open) => {
                if (!open) {
                  closeSubmenu();
                  // Fix stuck overlay issue on mobile
                  document.body.style.overflow = "auto";
                  document.body.style.pointerEvents = "auto";
                }
              }}
            >
              <SheetContent side="left" className="w-70 p-0">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu</SheetTitle>
                  <SheetDescription>Navigation menu</SheetDescription>
                </SheetHeader>
                <div className="flex flex-col justify-between mmx-sidebar p-4">
                  <Submenu
                    itemsLoading={itemsLoading}
                    items={submenuState.items}
                    title={submenuState.title}
                    placeholder={submenuState.placeholder}
                    createNew={submenuState.createNew}
                    onClick={(item, type) => {
                      submenuState.onClick?.(item, type ?? "");
                    }}
                    queryOptions={submenuState.queryOptions}
                  />
                </div>
              </SheetContent>
            </Sheet>
          )}

          {/* Right sidebar sheet for mobile */}
          {rightSidebar && (
            <Sheet open={!!rightSidebar} onOpenChange={(open) => !open && setRightSidebar(null)}>
              <SheetContent side="right" className="w-full sm:w-100 p-0" hideCloseButton={true}>
                <SheetHeader className="sr-only">
                  <SheetTitle>Details</SheetTitle>
                  <SheetDescription>Additional information</SheetDescription>
                </SheetHeader>
                <div className="p-2">
                  <div className="pr-3 w-full flex justify-end gap-1.5 mb-2">
                    <Button
                      variant="outline"
                      size="icon"
                      title="Close sidebar"
                      onClick={() =>
                        handleCloseParam({
                          paramToRemove: rightSidebarParamKey,
                          setSidebar: setRightSidebar,
                          navigate,
                        })
                      }
                    >
                      <X />
                    </Button>
                  </div>
                  <div className="flex flex-col h-[calc(100vh-100px)] overflow-hidden">{rightSidebar}</div>
                </div>
              </SheetContent>
            </Sheet>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="w-full mmx-content flex flex-col h-full">
              <div className="h-full overflow-y-auto">
                <div className="p-3 sm:p-4 flex flex-col gap-3 sm:gap-4">
                  <div className="flex shrink-0 items-center">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <DynamicBreadcrumbs />
                    </div>
                  </div>
                  {children}
                </div>
              </div>
            </div>
          </div>
          <GlobalFullscreenDialog />
        </DashboardContext.Provider>
      </div>
    );
  }

  // Desktop view
  return (
    <div className="w-full flex h-full relative">
      <DashboardContext.Provider value={contextValue}>
        <ResizablePanelGroup direction="horizontal" autoSaveId="dashboard-layout">
          {/* Only show submenu on desktop - wait for mobile detection to avoid flash */}
          {submenuState?.active && isMobile === false && <SubmenuPanel />}
          <ResizablePanel
            id="main-content-panel"
            order={2}
            className="mmx-content-right overflow-y-auto"
            defaultSize={mainContentSize}
          >
            <div className="w-full mmx-content flex flex-col h-full">
              <div id="main-content-container" className="h-full overflow-y-auto">
                <div className="p-4 flex flex-col gap-4">
                  <div className="flex shrink-0 items-center">
                    <div className="flex items-center gap-2 mb-3">
                      <DynamicBreadcrumbs />
                    </div>
                  </div>
                  {children}
                </div>
              </div>
            </div>
          </ResizablePanel>
          <RightSidebarPanel />
        </ResizablePanelGroup>
        <GlobalFullscreenDialog />
      </DashboardContext.Provider>
    </div>
  );
}
